// Modal de registro de refund manual (Story 4.11).
// Trigger: botão "Registrar reembolso" no detalhe de charge paga.

import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { createRefundSchema, type CreateRefundRequest } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { formatAmountBR } from '../charges/charge-display';
import { Field, Input, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useCreateRefund } from '../../hooks/use-refunds';
import { extractErrorMessage } from '../../lib/errors';
import { firstErrorMessage } from '../../lib/form-errors';

interface RefundModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charge: { id: string; clientName: string; description: string } | null;
  /** Saldo disponível em string (Decimal serializado). Default '0' se ainda carregando. */
  remainingBalance: string;
}

function defaultRefundedAt(): string {
  // <input type="datetime-local"> espera "YYYY-MM-DDTHH:mm" SEM timezone.
  const now = new Date();
  const tzOffsetMin = now.getTimezoneOffset();
  const local = new Date(now.getTime() - tzOffsetMin * 60_000);
  return local.toISOString().slice(0, 16);
}

export function RefundModal({
  open,
  onOpenChange,
  charge,
  remainingBalance,
}: RefundModalProps): JSX.Element {
  const mutation = useCreateRefund();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateRefundRequest>({
    resolver: zodResolver(createRefundSchema),
    defaultValues: {
      chargeId: charge?.id ?? '',
      type: 'manual',
      amount: Number(remainingBalance) || undefined,
      refundedAt: defaultRefundedAt(),
      reason: '',
      externalReference: '',
    },
  });

  async function onSubmit(values: CreateRefundRequest): Promise<void> {
    if (!charge) return;
    try {
      // refundedAt vem do <input type="datetime-local"> como 'YYYY-MM-DDTHH:mm' sem TZ.
      // new Date(...) interpreta no fuso do browser; .toISOString() converte pra UTC.
      // Assume operador acessando do BR (uso interno OOW). Se a operação for de outro
      // fuso, o ISO refletirá o wall-clock daquele fuso — não o BR.
      const refundedAtIso = new Date(values.refundedAt).toISOString();
      await mutation.mutateAsync({
        ...values,
        chargeId: charge.id,
        type: 'manual',
        refundedAt: refundedAtIso,
      });
      toast.success('Reembolso registrado', {
        description: 'Lembrete: Asaas não foi notificado — conciliação manual mensal.',
      });
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao registrar reembolso'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[12px] border border-line bg-panel p-6 shadow-2xl',
          )}
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em]">
                Registrar reembolso
              </Dialog.Title>
              {charge && (
                <Dialog.Description className="mt-1 text-[12px] text-muted">
                  {charge.clientName} · {charge.description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[8px] p-1.5 text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* AC8 — aviso obrigatório sobre conciliação manual */}
          <div className="mb-5 flex items-start gap-2 rounded-[8px] border border-warn/30 bg-warn/5 p-3">
            <AlertTriangle size={14} strokeWidth={1.8} className="mt-0.5 shrink-0 text-warn" />
            <p className="text-[12px] leading-[1.5] text-ink-2">
              <span className="font-medium">Refund manual:</span> Asaas{' '}
              <strong>não foi notificado</strong>. Faça transferência manual (PIX/TED) e registre
              aqui. Conciliação contábil necessária.
            </p>
          </div>

          {/* Saldo disponível */}
          <div className="mb-5 rounded-[8px] border border-line bg-panel-2 p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-3">
              Saldo disponível pra reembolso
            </div>
            <div className="mt-1 text-[16px] font-semibold tnum">
              {formatAmountBR(remainingBalance)}
            </div>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit, (errs) =>
              toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
            )}
            className="flex flex-col gap-4"
          >
            <Field
              label="Valor reembolsado (R$)"
              required
              error={errors.amount?.message}
              hint={`Máximo: ${formatAmountBR(remainingBalance)}`}
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="999999.99"
                  tabular
                  hasError={Boolean(errors.amount)}
                  {...register('amount', { valueAsNumber: true })}
                />
              )}
            </Field>

            <Field
              label="Data do reembolso"
              required
              error={errors.refundedAt?.message}
              hint="Data real em que a transferência foi feita"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="datetime-local"
                  hasError={Boolean(errors.refundedAt)}
                  {...register('refundedAt')}
                />
              )}
            </Field>

            <Field
              label="Motivo"
              required
              error={errors.reason?.message}
              hint="Por que esse reembolso? (auditoria obrigatória)"
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  maxLength={500}
                  placeholder="Ex: Cliente cancelou em até 7 dias / serviço não prestado"
                  hasError={Boolean(errors.reason)}
                  {...register('reason')}
                />
              )}
            </Field>

            <Field
              label="Referência"
              required
              error={errors.externalReference?.message}
              hint="ID PIX devolução, comprovante TED (auditoria fiscal)"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="text"
                  maxLength={200}
                  placeholder="Ex: PIX_DEVOL_847263 / TED 9988"
                  hasError={Boolean(errors.externalReference)}
                  {...register('externalReference')}
                />
              )}
            </Field>

            <Field
              label="URL do anexo (opcional)"
              error={errors.attachmentUrl?.message}
              hint="Cole o link do comprovante (Drive / S3 / WhatsApp)"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="url"
                  placeholder="https://..."
                  maxLength={1000}
                  hasError={Boolean(errors.attachmentUrl)}
                  {...register('attachmentUrl')}
                />
              )}
            </Field>

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={mutation.isPending}>
                {mutation.isPending ? 'Registrando…' : 'Confirmar reembolso'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
